import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";

/** DnA truncates anything larger server-side, so the input is capped to match. */
const MAX_PAGE_SIZE = 1000;

type UpdateField = (key: string, value: string | number) => void;
type AdvancedProps = ConnectorFormProps & { updateField: UpdateField };

function DatasetOverrides(props: AdvancedProps) {
  const bronzeDataset = (props.vars.bronzeDataset as string) ?? "";
  const silverDataset = (props.vars.silverDataset as string) ?? "";
  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <Label htmlFor="dna-bronze-dataset" className="form-label">
          Bronze Dataset
        </Label>
        <Input
          id="dna-bronze-dataset"
          type="text"
          value={bronzeDataset}
          onChange={(e) => props.updateField("bronzeDataset", e.target.value)}
          placeholder={`Default: bronze__${props.connectorPrefix}`}
          className="font-mono"
        />
      </div>
      <div className="flex-1">
        <Label htmlFor="dna-silver-dataset" className="form-label">
          Silver Dataset
        </Label>
        <Input
          id="dna-silver-dataset"
          type="text"
          value={silverDataset}
          onChange={(e) => props.updateField("silverDataset", e.target.value)}
          placeholder={`Default: silver__${props.connectorPrefix}`}
          className="font-mono"
        />
      </div>
    </div>
  );
}

function DnAAdvanced(props: AdvancedProps) {
  const oauthUrl = (props.vars.oauthUrl as string) ?? "";
  const pageSize = typeof props.vars.pageSize === "number" ? props.vars.pageSize : "";

  return (
    <ConnectorFormDetails
      summary="Advanced Options"
      className="mt-4 rounded border border-border/50"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="dna-page-size" className="form-label">
            Page Size
          </Label>
          <Input
            id="dna-page-size"
            type="number"
            max={MAX_PAGE_SIZE}
            value={pageSize}
            onChange={(e) => {
              const val = e.target.value;
              props.updateField("pageSize", val ? Math.min(Number(val), MAX_PAGE_SIZE) : "");
            }}
            placeholder="Default: 500 (max 1000)"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            DnA silently caps responses at {MAX_PAGE_SIZE} rows.
          </p>
        </div>
        <div>
          <Label htmlFor="dna-oauth-url" className="form-label">
            OAuth Token URL
          </Label>
          <Input
            id="dna-oauth-url"
            type="text"
            value={oauthUrl}
            onChange={(e) => props.updateField("oauthUrl", e.target.value)}
            placeholder="Derived from Base URL — override only if the instance differs"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Defaults to the site root plus <code>?OAuth2_AccessToken</code>, which is where DnA
            serves tokens.
          </p>
        </div>
        <DatasetOverrides {...props} />
      </div>
    </ConnectorFormDetails>
  );
}

export function DnAForm(props: ConnectorFormProps) {
  const tables = Array.isArray(props.vars.tables) ? (props.vars.tables as TableConfig[]) : [];
  const schedules = (
    props.vars.schedules != null &&
    typeof props.vars.schedules === "object" &&
    !Array.isArray(props.vars.schedules)
      ? props.vars.schedules
      : {}
  ) as Schedules;

  const updateField: UpdateField = (key, value) => {
    const next = { ...props.vars };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  };

  return (
    <>
      {/* Base URL is rendered once by ConnectorDetails, as for every other connector. */}
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />

      {!props.hideAdvanced && <DnAAdvanced {...props} updateField={updateField} />}
    </>
  );
}
